/** Delade typer och texter för omdömen från föräldrar. Klientsäker fil. */

export type TestimonialLocale = "sv" | "en" | "es";

export const TESTIMONIAL_STATUSES = ["draft", "submitted", "approved", "published", "rejected"] as const;
export type TestimonialStatus = (typeof TESTIMONIAL_STATUSES)[number];

export const TESTIMONIAL_STATUS_LABEL: Record<TestimonialStatus, string> = {
  draft: "Påbörjad",
  submitted: "Ny – väntar granskning",
  approved: "Godkänd",
  published: "Publicerad",
  rejected: "Avvisad",
};

export const INVITE_STATUSES = ["sent", "opened", "started", "submitted", "revoked", "external"] as const;
export type InviteStatus = (typeof INVITE_STATUSES)[number];

export const INVITE_STATUS_LABEL: Record<InviteStatus, string> = {
  sent: "Skickad",
  opened: "Öppnad",
  started: "Påbörjad",
  submitted: "Inskickad",
  revoked: "Återkallad",
  external: "Annan kanal",
};

/** Vanliga vägar ett omdöme kan komma in på när det läggs in för hand. */
export const SOURCE_CHANNELS = [
  "Mejl",
  "WhatsApp",
  "Telefon",
  "På plats",
  "Sociala medier",
] as const;

export const IMAGE_BACKGROUNDS = ["city", "beach", "indoor"] as const;
export type ImageBackground = (typeof IMAGE_BACKGROUNDS)[number];

export type Testimonial = {
  id: string;
  rating: number;
  quote: string;
  firstName: string;
  lastName: string;
  roleLabel: string;
  imageUrl: string;
  locale: string;
  isTest: boolean;
  status: TestimonialStatus;
  createdAt: string;
  /** När omdömet publicerades – används för datum och sortering publikt. */
  publishedAt?: string | null;
  /** Om omdömet får visas på startsidan (påverkar inte omdömessidan). */
  showOnHome?: boolean;
  /** Var omdömet kommer ifrån, t.ex. "instagram". */
  source?: string;
};

export type TestimonialAdminRow = Testimonial & {
  email: string;
  quoteSource: string;
  aiPrompt: string;
  aiDraft: string;
  editedQuote: string;
  originalImageUrl: string;
  imageChoice: string;
  imageBackground: string;
  imageVariants: string[];
  consentGiven: boolean;
  consentAt: string | null;
  consentText: string;
  adminNote: string;
  /** Varifrån omdömet kom, t.ex. Mejl eller WhatsApp. Tomt = kom via inbjudan. */
  sourceChannel: string;
  sortOrder: number;
  publishedAt: string | null;
  inviteId: string | null;
  updatedAt: string;
  /** Samma omdöme på flera språk delar denna grupp. */
  translationGroupId: string;
  socialConsent: boolean;
  socialConsentText: string;
  /** Privat förbättringsförslag från föräldern (vid betyg ≤4) – visas aldrig publikt. */
  improvementFeedback: string;
  displayPrefs: {
    firstName: boolean;
    lastName: boolean;
    role: boolean;
  };
};

/** Ett omdöme med alla sina språkversioner. */
export type TestimonialGroup = {
  groupId: string;
  rows: TestimonialAdminRow[];
};

/** Grupperar adminrader så språkversioner hör ihop som ett omdöme. */
export function groupTestimonials(rows: TestimonialAdminRow[]): TestimonialGroup[] {
  const order = ["sv", "en", "es"];
  const map = new Map<string, TestimonialAdminRow[]>();
  for (const row of rows) {
    const key = row.translationGroupId || row.id;
    const list = map.get(key);
    if (list) list.push(row);
    else map.set(key, [row]);
  }
  return [...map.entries()].map(([groupId, list]) => ({
    groupId,
    rows: [...list].sort((a, b) => order.indexOf(a.locale) - order.indexOf(b.locale)),
  }));
}

export function displayName(t: { firstName: string; lastName: string }): string {
  return [t.firstName, t.lastName].filter(Boolean).join(" ").trim();
}

/** Påståenden som föräldern kryssar i. Admin kan ändra listan i inställningarna. */
export const DEFAULT_STATEMENTS: Record<TestimonialLocale, string[]> = {
  sv: [
    "Vi är väldigt nöjda med skolan",
    "Vi skulle gärna rekommendera skolan till andra familjer",
    "Vårt barn trivs väldigt bra i skolan",
    "Lärarna är engagerade och bryr sig om barnen",
    "Vårt barn tycker om att gå till skolan",
    "Vi känner oss trygga med att ha vårt barn på skolan",
    "Det är en fin och familjär gemenskap på skolan",
    "Undervisningen håller en hög kvalitet",
    "Vårt barn får bra stöd och uppmärksamhet från lärarna",
    "Vi upplever att vårt barn utvecklas bra i skolan",
    "Vi har alltid fått ett bra bemötande av lärare och personal",
    "De mindre grupperna gör att lärarna har tid för varje barn",
    "Vårt barn känner sig tryggt i skolan",
    "Skolan har motsvarat våra förväntningar",
    "Kommunikationen mellan skolan och oss föräldrar fungerar bra",
    "Skolmiljön är trygg och trevlig",
    "Skolan erbjuder bra aktiviteter utöver undervisningen",
    "Skolan har ett väldigt bra läge",
    "Maten på skolan är bra",
    "Hämtning och lämning fungerar smidigt",
  ],
  en: [
    "We are very happy with the school",
    "We would gladly recommend the school to other families",
    "Our child is really thriving at the school",
    "The teachers are committed and care about the children",
    "Our child enjoys going to school",
    "We feel safe having our child at the school",
    "There is a warm, family-like community at the school",
    "The teaching is of a high standard",
    "Our child gets good support and attention from the teachers",
    "We can see that our child is developing well at the school",
    "We have always been met kindly by teachers and staff",
    "The smaller groups mean the teachers have time for every child",
    "Our child feels safe at school",
    "The school has lived up to our expectations",
    "Communication between the school and us parents works well",
    "The school environment is safe and pleasant",
    "The school offers good activities beyond the lessons",
    "The school has a very good location",
    "The food at school is good",
    "Drop-off and pick-up work smoothly",
  ],
  es: [
    "Estamos muy contentos con el colegio",
    "Recomendaríamos el colegio a otras familias",
    "Nuestro hijo o hija está muy a gusto en el colegio",
    "Los profesores están implicados y se preocupan por los niños",
    "A nuestro hijo o hija le gusta ir al colegio",
    "Nos sentimos tranquilos teniendo a nuestro hijo o hija en el colegio",
    "Hay un ambiente cercano y familiar en el colegio",
    "La enseñanza es de alta calidad",
    "Nuestro hijo o hija recibe buen apoyo y atención de los profesores",
    "Vemos que nuestro hijo o hija se desarrolla bien en el colegio",
    "Siempre nos han atendido bien los profesores y el personal",
    "Los grupos reducidos hacen que los profesores tengan tiempo para cada niño",
    "Nuestro hijo o hija se siente seguro en el colegio",
    "El colegio ha cumplido nuestras expectativas",
    "La comunicación entre el colegio y las familias funciona bien",
    "El entorno escolar es seguro y agradable",
    "El colegio ofrece buenas actividades más allá de las clases",
    "El colegio está muy bien situado",
    "La comida del colegio es buena",
    "La entrada y la salida funcionan con fluidez",
  ],
};

/** Färdiga formuleringar som föräldern kan utgå från. */

export const PRESET_QUOTES: Record<TestimonialLocale, string[]> = {
  sv: [
    "Små klasser och lärare som verkligen ser varje barn – vår familj har känt sig trygg från första dagen.",
    "Svensk läroplan mitt i Spanien gjorde flytten enkel för oss, och barnen har fått både språk och nya vänner.",
    "Kombinationen av skola, spanska varje dag och idrott på eftermiddagen är precis vad vi hoppades på.",
    "Korta beslutsvägar och nära kontakt med lärarna – vi får alltid svar samma dag.",
  ],
  en: [
    "Small classes and teachers who really see each child – our family felt at home from day one.",
    "The Swedish curriculum in Spain made the move simple for us, and the children gained both language and friends.",
    "School, Spanish every day and sport in the afternoon – exactly what we hoped for.",
    "Short lines of communication and close contact with the teachers; we always get an answer the same day.",
  ],
  es: [
    "Grupos reducidos y profesores que ven de verdad a cada niño: nos sentimos como en casa desde el primer día.",
    "El currículo sueco en España nos facilitó la mudanza, y los niños ganaron idioma y amigos.",
    "Colegio, español cada día y deporte por la tarde: justo lo que esperábamos.",
    "Comunicación directa y cercana con los profesores; siempre recibimos respuesta el mismo día.",
  ],
};

export const BACKGROUND_LABEL: Record<TestimonialLocale, Record<ImageBackground, string>> = {
  sv: { city: "Stadsmiljö", beach: "Strand", indoor: "Inomhusmiljö" },
  en: { city: "City", beach: "Beach", indoor: "Indoors" },
  es: { city: "Ciudad", beach: "Playa", indoor: "Interior" },
};

export const CONSENT_TEXT: Record<TestimonialLocale, string> = {
  sv: "Jag godkänner att Swedish International School publicerar mitt omdöme, mitt namn och min bild på skolans webbplats, även översatt till skolans övriga språk (svenska, engelska och spanska). Jag kan när som helst be om att det tas bort.",
  en: "I agree that Swedish International School may publish my review, my name and my photo on the school website, including translations into the school's other languages (Swedish, English and Spanish). I can ask for it to be removed at any time.",
  es: "Autorizo a Swedish International School a publicar mi opinión, mi nombre y mi foto en el sitio web del colegio, también traducida a los demás idiomas del colegio (sueco, inglés y español). Puedo solicitar su retirada en cualquier momento.",
};

/** Frivilligt extra samtycke: omdömet får även delas i sociala medier. */
export const SOCIAL_CONSENT_TEXT: Record<TestimonialLocale, string> = {
  sv: "Jag godkänner även att skolan delar mitt omdöme i sina sociala medier.",
  en: "I also agree that the school may share my review on its social media.",
  es: "También autorizo al colegio a compartir mi opinión en sus redes sociales.",
};

/** Tre färdiga hälsningar att utgå från när man bjuder in en förälder. */
export const INVITE_MESSAGE_TEMPLATES: Record<
  TestimonialLocale,
  { id: string; label: string; text: string }[]
> = {
  sv: [
    {
      id: "warm",
      label: "Kort och varm",
      text: "Vi är så glada att ha er familj hos oss. Om ni har några minuter över skulle det betyda mycket för oss om ni ville dela några rader om hur ni upplever skolan.",
    },
    {
      id: "loyal",
      label: "Ni har varit med ett tag",
      text: "Ni har följt skolan under en tid nu och har verkligen sett hur vardagen fungerar. Era ord skulle hjälpa andra familjer som funderar på att flytta hit att förstå hur det är på riktigt.",
    },
    {
      id: "brief",
      label: "Kort och saklig",
      text: "Vi samlar in omdömen från våra familjer. Det tar ett par minuter och ni bestämmer själva vad som får publiceras.",
    },
  ],
  en: [
    {
      id: "warm",
      label: "Short and warm",
      text: "We are so glad to have your family with us. If you have a few minutes, it would mean a lot to us if you shared a few words about how you experience the school.",
    },
    {
      id: "loyal",
      label: "You have been with us a while",
      text: "You have been part of the school for a while now and really know how everyday life works here. Your words would help other families considering the move understand what it is really like.",
    },
    {
      id: "brief",
      label: "Short and factual",
      text: "We are collecting reviews from our families. It takes a couple of minutes, and you decide yourself what may be published.",
    },
  ],
  es: [
    {
      id: "warm",
      label: "Breve y cercano",
      text: "Nos alegra mucho tener a vuestra familia con nosotros. Si disponéis de unos minutos, significaría mucho para nosotros que compartierais unas líneas sobre vuestra experiencia en el colegio.",
    },
    {
      id: "loyal",
      label: "Lleváis tiempo con nosotros",
      text: "Lleváis un tiempo formando parte del colegio y conocéis bien el día a día. Vuestras palabras ayudarían a otras familias que se plantean mudarse aquí a entender cómo es de verdad.",
    },
    {
      id: "brief",
      label: "Breve y directo",
      text: "Estamos recogiendo opiniones de nuestras familias. Solo lleva un par de minutos y vosotros decidís qué se puede publicar.",
    },
  ],
};

